<template>
  <div class="container mt-4">
    <div class="card">
      <div class="card-header">
        <h3>{{ isEditing ? 'Edit Shift' : 'Add New Shift' }}</h3>
      </div>
      <div class="card-body">
        <form @submit.prevent="submitForm">
          <div class="row">
            <!-- Left Column -->
            <div class="col-md-6">
              <div class="mb-3">
                <label for="shiftName" class="form-label">Shift Name</label>
                <input
                  type="text"
                  class="form-control"
                  id="shiftName"
                  v-model="shift.name"
                  :class="{ 'is-invalid': errors.name }"
                  required
                >
                <div class="invalid-feedback" v-if="errors.name">
                  {{ errors.name }}
                </div>
                <small class="form-text text-muted">Example: Morning Shift, Night Shift, Evening Shift</small>
              </div>

              <div class="mb-3">
                <label for="departmentSelect" class="form-label">Department</label>
                <select
                  class="form-select"
                  id="departmentSelect"
                  v-model="shift.department_id"
                  :class="{ 'is-invalid': errors.department_id }"
                  required
                >
                  <option value="" disabled selected>Select a Department</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.department_id">
                  {{ errors.department_id }}
                </div>
              </div>

              <div class="mb-3">
                <label for="startTime" class="form-label">Start Time</label>
                <input
                  type="time"
                  class="form-control"
                  id="startTime"
                  v-model="shift.start_time"
                  :class="{ 'is-invalid': errors.start_time }"
                  @change="calculateDuration"
                  required
                >
                <div class="invalid-feedback" v-if="errors.start_time">
                  {{ errors.start_time }}
                </div>
              </div>

              <div class="mb-3">
                <label for="endTime" class="form-label">End Time</label>
                <input
                  type="time"
                  class="form-control"
                  id="endTime"
                  v-model="shift.end_time"
                  :class="{ 'is-invalid': errors.end_time }"
                  @change="calculateDuration"
                  required
                >
                <div class="invalid-feedback" v-if="errors.end_time">
                  {{ errors.end_time }}
                </div>
              </div>
            </div>

            <!-- Right Column -->
            <div class="col-md-6">
              <div class="mb-3">
                <label for="duration" class="form-label">Duration</label>
                <div class="input-group">
                  <input
                    type="number"
                    class="form-control"
                    id="duration"
                    v-model="shift.duration_minutes"
                    :class="{ 'is-invalid': errors.duration_minutes }"
                    disabled
                  >
                  <span class="input-group-text">minutes</span>
                </div>
                <div class="invalid-feedback" v-if="errors.duration_minutes">
                  {{ errors.duration_minutes }}
                </div>
                <div class="form-text" v-if="shift.duration_minutes">
                  {{ formatDuration(shift.duration_minutes) }}
                </div>
              </div>

              <div class="mb-3 form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="isOvernight"
                  v-model="shift.is_overnight"
                >
                <label class="form-check-label" for="isOvernight">This is an overnight shift</label>
                <div class="form-text text-muted mt-1">
                  Check this if the shift spans across midnight (e.g., 10:00 PM to 6:00 AM)
                </div>
              </div>

              <div class="mb-3">
                <label for="description" class="form-label">Description</label>
                <textarea
                  class="form-control"
                  id="description"
                  rows="4"
                  v-model="shift.description"
                  :class="{ 'is-invalid': errors.description }"
                ></textarea>
                <div class="invalid-feedback" v-if="errors.description">
                  {{ errors.description }}
                </div>
                <small class="form-text text-muted">Provide details about this shift such as responsibilities, break times, etc.</small>
              </div>
            </div>
          </div>

          <!-- Duration Alert for Overnight Shifts -->
          <div v-if="showOvernightAlert" class="alert alert-info mb-3">
            <i class="fas fa-info-circle me-2"></i>
            Since this is an overnight shift, the duration is calculated across midnight.
          </div>

          <div class="d-flex justify-content-between mt-3">
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Shift' : 'Save Shift' }}
            </button>
            <router-link :to="{ name: 'ShiftList' }" class="btn btn-secondary">Cancel</router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShiftAdd',
  data() {
    return {
      shift: {
        name: '',
        department_id: '',
        start_time: '',
        end_time: '',
        duration_minutes: 0,
        is_overnight: false,
        description: ''
      },
      departments: [],
      errors: {},
      isSubmitting: false,
      isEditing: false,
      showOvernightAlert: false
    }
  },
  methods: {
    resetErrors() {
      this.errors = {}
    },
    
    calculateDuration() {
      if (!this.shift.start_time || !this.shift.end_time) {
        this.shift.duration_minutes = 0;
        return;
      }
      
      // Parse start and end times
      const [startHours, startMinutes] = this.shift.start_time.split(':').map(Number);
      const [endHours, endMinutes] = this.shift.end_time.split(':').map(Number);
      
      // Convert to minutes since midnight
      const startTimeInMinutes = startHours * 60 + startMinutes;
      let endTimeInMinutes = endHours * 60 + endMinutes;
      
      // Handle overnight shifts
      if (this.shift.is_overnight || endTimeInMinutes < startTimeInMinutes) {
        this.shift.is_overnight = true;
        this.showOvernightAlert = true;
        endTimeInMinutes += 24 * 60; // Add 24 hours worth of minutes
      } else {
        this.showOvernightAlert = false;
      }
      
      // Calculate duration
      this.shift.duration_minutes = endTimeInMinutes - startTimeInMinutes;
    },
    
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return '';
      
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      
      let result = '';
      if (hours === 1) result += '1 hour';
      else if (hours > 1) result += `${hours} hours`;
      
      if (mins > 0) {
        if (result) result += ' and ';
        result += `${mins} minute${mins !== 1 ? 's' : ''}`;
      }
      
      return result;
    },
    
    async submitForm() {
      this.resetErrors();
      
      try {
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        
        // Validate form
        if (!this.shift.name) {
          this.errors.name = 'Shift name is required';
          this.isSubmitting = false;
          return;
        }
        
        if (!this.shift.department_id) {
          this.errors.department_id = 'Department is required';
          this.isSubmitting = false;
          return;
        }
        
        if (!this.shift.start_time) {
          this.errors.start_time = 'Start time is required';
          this.isSubmitting = false;
          return;
        }
        
        if (!this.shift.end_time) {
          this.errors.end_time = 'End time is required';
          this.isSubmitting = false;
          return;
        }
        
        // Recalculate duration just to be sure
        this.calculateDuration();
        
        if (this.shift.duration_minutes <= 0) {
          this.errors.duration_minutes = 'Duration must be positive';
          this.isSubmitting = false;
          return;
        }
        
        // Submit the form
        if (this.isEditing) {
          await this.$api.put(`/shifts/${this.$route.params.id}`, this.shift);
          this.$toast.success('Shift updated successfully!');
        } else {
          await this.$api.post('/shifts', this.shift);
          this.$toast.success('Shift created successfully!');
        }
        
        // Redirect back to the list
        this.$router.push({name: 'ShiftList'});
      } catch (error) {
        console.error(`Error ${this.isEditing ? 'updating' : 'creating'} shift:`, error);
        
        if (error.response?.data?.errors) {
          // Handle validation errors from the API
          const apiErrors = error.response.data.errors;
          
          if (apiErrors.name) this.errors.name = apiErrors.name[0];
          if (apiErrors.department_id) this.errors.department_id = apiErrors.department_id[0];
          if (apiErrors.start_time) this.errors.start_time = apiErrors.start_time[0];
          if (apiErrors.end_time) this.errors.end_time = apiErrors.end_time[0];
          if (apiErrors.duration_minutes) this.errors.duration_minutes = apiErrors.duration_minutes[0];
          if (apiErrors.description) this.errors.description = apiErrors.description[0];
        } else {
          // Generic error toast
          this.$toast.error(`Failed to ${this.isEditing ? 'update' : 'create'} shift. Please try again.`);
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    
    async fetchDepartments() {
      try {
        const response = await this.$api.get('/departments');
        this.departments = response.data.data;
      } catch (error) {
        console.error('Error fetching departments:', error);
        this.$toast.error('Could not load departments. Please try again.');
      }
    },
    
    async loadShift() {
      try {
        const response = await this.$api.get(`/shifts/${this.$route.params.id}`);
        this.shift = response.data.data;
        
        // Format times for input (HH:MM)
        if (this.shift.start_time) {
          this.shift.start_time = this.formatTimeForInput(this.shift.start_time);
        }
        
        if (this.shift.end_time) {
          this.shift.end_time = this.formatTimeForInput(this.shift.end_time);
        }
        
        this.isEditing = true;
        this.calculateDuration();
      } catch (error) {
        console.error('Error loading shift:', error);
        this.$toast.error('Failed to load shift details.');
        this.$router.push({name: 'ShiftList'});
      }
    },
    
    formatTimeForInput(timeString) {
      // Handle possible formats and return HH:MM
      if (!timeString) return '';
      
      // If it's already in HH:MM or HH:MM:SS format
      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
        // Return just HH:MM part
        return timeString.substring(0, 5);
      }
      
      // Try to parse as a date
      try {
        const date = new Date(timeString);
        return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      } catch (e) {
        return '';
      }
    }
  },
  mounted() {
    this.fetchDepartments();
    
    // If we have an ID parameter, load the shift data for editing
    if (this.$route.params.id) {
      this.loadShift();
    }
    
    // Initialize overnight status watcher
    this.$watch('shift.is_overnight', (newValue) => {
      if (newValue) {
        this.showOvernightAlert = true;
      } else {
        this.showOvernightAlert = false;
        this.calculateDuration(); // Recalculate without overnight adjustment
      }
    });
  }
}
</script>

<style scoped>
.container {
  max-width: 900px;
}

.card {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 15px 20px;
}

.card-header h3 {
  margin-bottom: 0;
  font-size: 1.5rem;
  color: #343a40;
}

.form-label {
  font-weight: 500;
  color: #495057;
}

.form-text {
  font-size: 0.85rem;
}

.alert-info {
  background-color: #e5f5ff;
  border-color: #d0ebff;
  color: #0077cc;
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

@media (max-width: 768px) {
  .container {
    padding: 0 15px;
  }
  
  .row {
    flex-direction: column;
  }
}
</style>
